import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { VirtualTourService } from '../../services/virtual-tour.service';
import { ButtonComponent } from "../button/button.component";

@Component({
  selector: 'app-virtual-tour',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './virtual-tour.component.html',
  styleUrl: './virtual-tour.component.scss',
})
export class VirtualTourComponent implements OnInit, OnDestroy {
  public tour = inject(VirtualTourService);
  private destroy$ = new Subject<void>();

  /* ---------- reactive helpers ------------------------------- */
  cardPos = signal<{ top: string; left: string }>({ top: '50%', left: '50%' });
  activeStep = computed(() => this.tour.steps()[this.tour.currentIndex()]);
  stepsSize = computed(() => this.tour.steps().length);

  /* ---------- lifecycle -------------------------------------- */
  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    /* re-place card on scroll / resize */
    merge(fromEvent(window, 'scroll'), fromEvent(window, 'resize'))
      .pipe(auditTime(16), takeUntil(this.destroy$))
      .subscribe(() => this.tour.isActive() && this.placeCard());

    /* re-place card whenever the index changes */
    effect(() => {
      this.tour.isActive() && this.placeCard();
    });
  }

  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  // This method is used to handle esc event
  

  /* ---------- key-handler ------------------------------------ */
  @HostListener('document:keydown.escape')
  onEsc() { this.tour.finish(); }

  /* ---------- core position logic ---------------------------- */
  // This method is used to place card
  placeCard() {
    const step = this.activeStep();
    const el = document.querySelector(step.target) as HTMLElement | null;
    if (!el) return;

    const GAP = 12;
    const rect = el.getBoundingClientRect();
    let top = rect.top + window.scrollY;
    let left = rect.left + window.scrollX;

    switch (step.placement ?? 'bottom') {
      case 'top': top -= GAP; break;
      case 'bottom': top += rect.height + GAP; break;
      case 'left': left -= GAP; break;
      case 'right': left += rect.width + GAP; break;
      case 'center': top += rect.height / 2;
        left += rect.width / 2; break;
    }
    this.cardPos.set({ top: `${top}px`, left: `${left}px` });
  }

  /* EXTRA computed property – Tailwind classes for the arrow */
  // This method is used to arrow class
  get arrowClass() {
    const place = this.activeStep().placement ?? 'bottom'; // default
    return {
      /* shared utilities already in template:
         w-4 h-4 absolute rotate-45 bg-white shadow ring-1 … */

      /* centre the arrow on the chosen edge */
      'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2': place === 'bottom',
      'bottom-0 left-1/2 -translate-x-1/2  translate-y-1/2': place === 'top',

      'left-0  top-1/2 -translate-y-1/2 -translate-x-1/2': place === 'right',
      'right-0 top-1/2 -translate-y-1/2  translate-x-1/2': place === 'left',

      /* centre (no arrow) */
      'hidden': place === 'center'
    };
  };

  // This method is used to handle outside
  handleOutside(): void {
    const nextIndex = this.tour.currentIndex() + 1;
    nextIndex < this.tour.steps().length
      ? this.tour.next()     // go to next step
      : this.tour.finish();  // no more steps → close tour
  }
}
